import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, KeyRound, Lock, Power, Mail, Phone, Building2, ShieldCheck, Clock, Calendar, AlertCircle, X, MapPin } from 'lucide-react';

const f = "'Inter',ui-sans-serif,system-ui,sans-serif";

const ROLE_COLORS = {
  'hr_executive':    { bg: '#EDE9FE', color: '#7C3AED', label: 'HR Executive' },
  'user':            { bg: '#DBEAFE', color: '#2563EB', label: 'Employee' },
  'hr_manager':      { bg: '#D1FAE5', color: '#059669', label: 'HR Manager' },
  'super_admin':    { bg: '#FEF3C7', color: '#D97706', label: 'System Admin' },
  'support_manager': { bg: '#FEE2E2', color: '#DC2626', label: 'Support Manager' },
};

function Avatar({ name, size = 64 }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#1E293B', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.33, fontWeight: 700, flexShrink: 0, letterSpacing: '0.02em' }}>
      {initials}
    </div>
  );
}

function InfoRow({ Icon, label, value, blue }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={14} color="#64748B" />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: blue ? '#3B82F6' : '#1E293B' }}>{value}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{ flex: 1, background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#1E293B', lineHeight: 1, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#94A3B8' }}>{sub}</div>
    </div>
  );
}

function Toggle({ on, onChange, disabled }) {
  return (
    <div onClick={() => !disabled && onChange(!on)}
      style={{ width: 42, height: 24, borderRadius: 999, background: on ? '#1E293B' : '#E2E8F0', cursor: disabled ? 'default' : 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#FFFFFF', position: 'absolute', top: 3, left: on ? 21 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

function AlertModal({ message, type = 'success', onClose }) {
  const isSuccess = type === 'success';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#FFFFFF', borderRadius: 16, width: 400, padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: f, textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: isSuccess ? '#D1FAE5' : '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          {isSuccess ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          )}
        </div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', margin: '0 0 8px' }}>
          {isSuccess ? 'Success' : 'Error'}
        </h3>
        <p style={{ fontSize: 14, color: '#64748B', margin: '0 0 20px', lineHeight: 1.5 }}>
          {message}
        </p>
        <button
          onClick={onClose}
          style={{
            width: '100%',
            height: 40,
            borderRadius: 8,
            border: 'none',
            background: isSuccess ? '#059669' : '#DC2626',
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: f
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editForm, setEditForm] = useState({ username: '', email: '', role: '', status: '', emp_code: '', employee_id: '' });
  const [passwordForm, setPasswordForm] = useState({ new_password: '' });
  const [saving, setSaving] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
        const res = await fetch('/api/admin/roles', { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        const roles = (data.data || []).map(r => ({ value: r.name, label: r.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }));
        setRoleOptions(roles);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
      }
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        const res = await fetch(`/api/admin/users/${id}`, { headers });
        if (!res.ok) throw new Error('Failed to fetch user');
        const data = await res.json();
        setUser(data.data);
        setEditForm({
          username: data.data.username || '',
          email: data.data.email || '',
          role: data.data.role || '',
          status: data.data.status || '',
          emp_code: data.data.emp_code || '',
          employee_id: data.data.employee_id || ''
        });
      } catch (err) {
        console.error('Error fetching user:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [id]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
        const res = await fetch('/api/v2/organization-locations/active', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setLocationOptions(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch locations:', err);
      }
    };
    fetchLocations();
  }, []);

  const handleEdit = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
      // Only send fields that have values and are supported by backend API
      const updateData = {};
      if (editForm.username) updateData.username = editForm.username;
      if (editForm.email) updateData.email = editForm.email;
      if (editForm.role) updateData.role = editForm.role;
      if (editForm.status) updateData.status = editForm.status;
      if (editForm.emp_code) updateData.emp_code = editForm.emp_code;
      if (editForm.employee_id) updateData.employee_id = editForm.employee_id;
      const res = await fetch(`/api/admin/users/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || errData.message || 'Failed to update user');
      }
      const data = await res.json();
      setUser({ ...user, ...editForm });
      setShowEditModal(false);
      setAlertModal({ show: true, message: 'User updated successfully', type: 'success' });
    } catch (err) {
      setAlertModal({ show: true, message: 'Failed to update user: ' + err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordForm.new_password) {
      setAlertModal({ show: true, message: 'Please enter a new password', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const token = sessionStorage.getItem('hcmsToken') || sessionStorage.getItem('token') || localStorage.getItem('hcmsToken') || localStorage.getItem('token');
      const res = await fetch(`/api/admin/users/${id}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: passwordForm.new_password })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || errData.message || 'Failed to reset password');
      }
      setShowPasswordModal(false);
      setPasswordForm({ new_password: '' });
      setAlertModal({ show: true, message: 'Password reset successfully', type: 'success' });
    } catch (err) {
      setAlertModal({ show: true, message: 'Failed to reset password: ' + err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const actionBtn = (extra = {}) => ({ height: 32, padding: '0 14px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: f, ...extra });

  if (loading) {
    return (
      <div style={{ fontFamily: f, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#64748B' }}>Loading user details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: f, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <AlertCircle size={48} color="#EF4444" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: '#EF4444', marginBottom: 8 }}>Error loading user</div>
          <div style={{ fontSize: 12, color: '#94A3B8' }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ fontFamily: f, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#64748B' }}>User not found</div>
        </div>
      </div>
    );
  }

  const rc = ROLE_COLORS[user.role] || { bg: '#F1F5F9', color: '#64748B', label: user.role };

  return (
    <div style={{ fontFamily: f }}>
      {/* Breadcrumb */}
      <button onClick={() => navigate('/hcms/admin-users')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#64748B', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 20, fontFamily: f }}>
        <ArrowLeft size={14} />Back to Users
      </button>

      {/* Profile card */}
      <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 16, padding: '24px 28px', boxShadow: '0 1px 2px rgba(15,23,42,0.04)', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar name={user.username || 'User'} size={64} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{user.employee_id || '—'}</div>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E293B', margin: '0 0 8px' }}>{user.username || '—'}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ padding: '3px 10px', borderRadius: 999, background: rc.bg, color: rc.color, fontSize: 12, fontWeight: 600 }}>{rc.label}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: user.status === 'active' ? '#D1FAE5' : '#F1F5F9', color: user.status === 'active' ? '#059669' : '#94A3B8', fontSize: 12, fontWeight: 600 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: user.status === 'active' ? '#059669' : '#94A3B8' }} />{user.status === 'active' ? 'Active' : user.status || 'Inactive'}
                </span>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowEditModal(true)} style={actionBtn()}><Pencil size={13} />Edit</button>
            <button onClick={() => setShowPasswordModal(true)} style={actionBtn()}><KeyRound size={13} />Reset Password</button>
            <button style={actionBtn({ color: '#EF4444', borderColor: '#FECACA', background: '#FFF5F5' })}><Power size={13} />Disable</button>
          </div>
        </div>

        {/* Info grid - 2 columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <InfoRow Icon={Mail}       label="Email"       value={user.email || '—'} blue />
          <InfoRow Icon={ShieldCheck} label="Role"       value={rc.label} />
          <InfoRow Icon={Building2} label="Employee Code" value={user.emp_code || '—'} />
          <InfoRow Icon={Calendar}   label="Employee ID" value={user.employee_id || '—'} />
          <InfoRow Icon={Calendar}   label="Organization ID" value={user.organization_id || '—'} />
          <InfoRow Icon={Clock}     label="Last Login"   value={user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'} />
          <InfoRow Icon={Calendar}   label="Created At"   value={user.created_at ? new Date(user.created_at).toLocaleString() : '—'} />
          <InfoRow Icon={Calendar}   label="Updated At"   value={user.updated_at ? new Date(user.updated_at).toLocaleString() : '—'} />
        </div>
      </div>

      {/* Stat cards - only show Tickets Raised for employees */}
      <div style={{ display: 'flex', gap: 16 }}>
        {(user.role === 'user' || user.role === 'employee') && (
          <StatCard label="Tickets Raised"  value={user.ticket_count || 0}        sub="Lifetime activity" />
        )}
        <StatCard label="Failed Attempts" value={user.failed_login_attempts || 0} sub="In the last 24 hours" />
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: f }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', margin: '0 0 4px' }}>Edit User</h2>
                <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>Update user information.</p>
              </div>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#94A3B8' }}><X size={18} /></button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Username</label><input value={editForm.username} onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))} style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B' }} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Email</label><input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B' }} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Role</label>
                  <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))} style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B', cursor: 'pointer', appearance: 'auto' }}>
                    <option value="">Select role</option>
                    {roleOptions.map(r => <option key={r.value || r} value={r.value || r}>{r.label || r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B', cursor: 'pointer', appearance: 'auto' }}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="locked">Locked</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Employee Code</label><input value={editForm.emp_code} onChange={e => setEditForm(f => ({ ...f, emp_code: e.target.value }))} style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B' }} /></div>
                <div><label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Employee ID</label><input value={editForm.employee_id} onChange={e => setEditForm(f => ({ ...f, employee_id: e.target.value }))} style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B' }} /></div>
              </div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowEditModal(false)} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#64748B', cursor: 'pointer', fontFamily: f }}>Cancel</button>
              <button onClick={handleEdit} disabled={saving} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: 'none', background: '#1E293B', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: f, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: f }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1E293B', margin: '0 0 4px' }}>Reset Password</h2>
                <p style={{ fontSize: 13, color: '#64748B', margin: 0 }}>Set a new password for this user.</p>
              </div>
              <button onClick={() => setShowPasswordModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#94A3B8' }}><X size={18} /></button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>New Password</label><input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} placeholder="Enter new password" style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, fontFamily: f, outline: 'none', boxSizing: 'border-box', color: '#1E293B' }} /></div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowPasswordModal(false)} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize: 13, fontWeight: 600, color: '#64748B', cursor: 'pointer', fontFamily: f }}>Cancel</button>
              <button onClick={handleResetPassword} disabled={saving} style={{ height: 36, padding: '0 18px', borderRadius: 8, border: 'none', background: '#1E293B', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: f, opacity: saving ? 0.6 : 1 }}>{saving ? 'Resetting...' : 'Reset Password'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal.show && (
        <AlertModal
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>
  );
}
